import React, { useCallback, useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, Link2 } from "lucide-react";

const fmt = (v) => Number(v || 0).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const STATUS = {
  matched: ["Abgestimmt", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  amount_diff: ["Betragsdifferenz", "bg-amber-50 text-amber-700 border-amber-200"],
  missing_bill: ["Kreditor fehlt", "bg-rose-50 text-rose-700 border-rose-200"],
  missing_invoice: ["Debitor fehlt", "bg-rose-50 text-rose-700 border-rose-200"],
};

const IcContactsPanel = ({ companyId }) => {
  const [data, setData] = useState({ contacts: [], group_companies: [] });
  const [filter, setFilter] = useState("");
  const load = useCallback(() => apiClient.get(`/companies/${companyId}/holding/ic-contacts`).then((r) => setData(r.data)).catch(() => {}), [companyId]);
  useEffect(() => { load(); }, [load]);

  const link = async (contactId, value) => {
    try {
      await apiClient.put(`/companies/${companyId}/holding/ic-contacts/${contactId}`, { intercompany_company_id: value === "none" ? null : value });
      toast.success(value === "none" ? "Verknüpfung entfernt" : "Kontakt als Gruppenfirma markiert");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };

  const linked = data.contacts.filter((c) => c.intercompany_company_id);
  const list = data.contacts.filter((c) => c.intercompany_company_id || (filter && c.name?.toLowerCase().includes(filter.toLowerCase()))).slice(0, 25);
  return (
    <div className="rounded-lg border border-slate-200 p-3" data-testid="ic-contacts-panel">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        <div className="text-sm font-medium text-slate-800 flex items-center gap-1.5"><Link2 className="h-4 w-4" /> IC-Kontakte ({linked.length} verknüpft)</div>
        <Input className="w-56 h-8" placeholder="Kontakt suchen…" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="ic-contact-search" />
      </div>
      <p className="text-[11px] text-slate-400 mb-2">Debitor/Kreditor, der eine Gruppenfirma ist → Rechnungen werden automatisch abgeglichen und konsolidiert eliminiert.</p>
      {list.length === 0 && <div className="text-xs text-slate-400">Suche einen Kontakt, um ihn einer Gruppenfirma zuzuordnen.</div>}
      <div className="space-y-1">
        {list.map((c) => (
          <div key={c.id} className="flex items-center gap-2 text-sm" data-testid={`ic-contact-row-${c.id}`}>
            <span className="flex-1 truncate">{c.name} <span className="text-xs text-slate-400">({c.type === "customer" ? "Kunde" : "Lieferant"})</span></span>
            <Select value={c.intercompany_company_id || "none"} onValueChange={(v) => link(c.id, v)}>
              <SelectTrigger className="w-56 h-8" data-testid={`ic-contact-select-${c.id}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— keine Gruppenfirma —</SelectItem>
                {data.group_companies.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        ))}
      </div>
    </div>
  );
};

export const IcReconciliationCard = ({ companyId, year }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try { setData((await apiClient.get(`/companies/${companyId}/holding/reconciliation`, { params: { year } })).data); }
    catch (e) { toast.error(e.response?.data?.detail || "Abgleich fehlgeschlagen"); }
    finally { setLoading(false); }
  };

  return (
    <Card className="p-4 space-y-4" data-testid="holding-reconciliation-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-semibold text-slate-900">Intercompany-Abgleich {year}</h2>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="holding-reconcile-btn">{loading ? "Prüfe…" : "Abgleich starten"}</Button>
      </div>
      <IcContactsPanel companyId={companyId} />
      {data && (
        <>
          <div className={`rounded-lg border p-3 text-sm flex items-center gap-2 ${data.ok ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-amber-50 border-amber-200 text-amber-800"}`} data-testid="reconcile-summary">
            {data.ok ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
            {data.ok ? "Alle konzerninternen Positionen stimmen überein." : `Differenzen total CHF ${fmt(data.difference_total)} · ${data.open_items} offene Rechnungs-Paare`}
          </div>
          <div className="overflow-x-auto border rounded-lg">
            <Table data-testid="reconcile-pairs-table">
              <TableHeader><TableRow>
                <TableHead>Firmenpaar</TableHead><TableHead className="text-right">Forderung A</TableHead><TableHead className="text-right">Verbindlichkeit B</TableHead>
                <TableHead className="text-right">Δ Bilanz</TableHead><TableHead className="text-right">Ertrag A</TableHead><TableHead className="text-right">Aufwand B</TableHead><TableHead className="text-right">Δ ER</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {data.pairs.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-400 text-sm">Keine konzerninternen Buchungen gefunden.</TableCell></TableRow>}
                {data.pairs.flatMap((p) => [
                  { key: `${p.company_a_id}-${p.company_b_id}`, a: p.company_a, b: p.company_b, rec: p.receivable_a, pay: p.payable_b, d1: p.balance_diff_ab, rev: p.revenue_a, exp: p.expense_b, d2: p.pl_diff_ab },
                  { key: `${p.company_b_id}-${p.company_a_id}`, a: p.company_b, b: p.company_a, rec: p.receivable_b, pay: p.payable_a, d1: p.balance_diff_ba, rev: p.revenue_b, exp: p.expense_a, d2: p.pl_diff_ba },
                ]).filter((r) => r.rec || r.pay || r.rev || r.exp).map((r) => (
                  <TableRow key={r.key} data-testid={`reconcile-pair-${r.key}`}>
                    <TableCell className="text-sm">{r.a} <span className="text-slate-400">→</span> {r.b}</TableCell>
                    <TableCell className="text-right font-mono text-sm">{fmt(r.rec)}</TableCell>
                    <TableCell className="text-right font-mono text-sm">{fmt(r.pay)}</TableCell>
                    <TableCell className={`text-right font-mono text-sm font-semibold ${Math.abs(r.d1) > 0.05 ? "text-rose-700" : "text-emerald-700"}`}>{fmt(r.d1)}</TableCell>
                    <TableCell className="text-right font-mono text-sm">{fmt(r.rev)}</TableCell>
                    <TableCell className="text-right font-mono text-sm">{fmt(r.exp)}</TableCell>
                    <TableCell className={`text-right font-mono text-sm font-semibold ${Math.abs(r.d2) > 0.05 ? "text-rose-700" : "text-emerald-700"}`}>{fmt(r.d2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {data.items.length > 0 && (
            <div className="overflow-x-auto border rounded-lg">
              <Table data-testid="reconcile-items-table">
                <TableHeader><TableRow>
                  <TableHead>Status</TableHead><TableHead>Von → An</TableHead><TableHead>Debitor-Rg.</TableHead><TableHead>Kreditor-Rg.</TableHead><TableHead className="text-right">Differenz</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {data.items.map((it, i) => (
                    <TableRow key={i} data-testid={`reconcile-item-${i}`}>
                      <TableCell><Badge variant="outline" className={`text-[10px] ${STATUS[it.status]?.[1]}`}>{STATUS[it.status]?.[0] || it.status}</Badge></TableCell>
                      <TableCell className="text-sm">{it.from_company} → {it.to_company}</TableCell>
                      <TableCell className="text-sm font-mono">{it.invoice_number ? `${it.invoice_number} · ${fmt(it.invoice_total)}` : "—"}</TableCell>
                      <TableCell className="text-sm font-mono">{it.bill_number != null ? `${it.bill_number || "?"} · ${fmt(it.bill_total)}` : "—"}</TableCell>
                      <TableCell className={`text-right font-mono text-sm ${Math.abs(it.difference) > 0.05 ? "text-rose-700" : ""}`}>{fmt(it.difference)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </>
      )}
    </Card>
  );
};
